'use client'

// React
import { useEffect, useRef, useState } from 'react'

// Animation
import { easeOut, motion, useMotionValue, useTransform } from 'framer-motion'

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQrcode } from '@fortawesome/free-solid-svg-icons'

// design-studio
import {
  ButtonLG,
  fadeInUp,
  SectionHeader,
  staggerContainer,
  TerminalBox,
} from 'design-studio'

// Utils
import { Join } from 'design-studio/lib'
import { NavigationData } from '@/site/data-links'

function StepBadge({ number }: { number: number }) {
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-slate-900">
      {number}
    </span>
  )
}

const TYPEWRITER_WORDS = ['function', 'behavior', 'logic', 'system', 'change']

function TypewriterWord() {
  const [displayed, setDisplayed] = useState('')
  const [wordIdx, setWordIdx] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    const word = TYPEWRITER_WORDS[wordIdx]
    if (!deleting) {
      if (displayed.length < word.length) {
        const delay = 75 + Math.random() * 65
        const t = setTimeout(
          () => setDisplayed(word.slice(0, displayed.length + 1)),
          delay,
        )
        return () => clearTimeout(t)
      } else {
        const t = setTimeout(() => setDeleting(true), 950 + Math.random() * 450)
        return () => clearTimeout(t)
      }
    } else {
      if (displayed.length > 0) {
        const delay = 42 + Math.random() * 33
        const t = setTimeout(() => setDisplayed((d) => d.slice(0, -1)), delay)
        return () => clearTimeout(t)
      } else {
        const t = setTimeout(
          () => {
            setWordIdx((i) => (i + 1) % TYPEWRITER_WORDS.length)
            setDeleting(false)
          },
          280 + Math.random() * 120,
        )
        return () => clearTimeout(t)
      }
    }
  }, [displayed, deleting, wordIdx])

  return (
    <span className="text-teal-400">
      {displayed}
      <span className="ml-px inline-block w-[2px] translate-y-px animate-pulse bg-teal-400 align-middle text-transparent select-none">
        |
      </span>
    </span>
  )
}

export function HeroHowSection() {
  const [mode, setMode] = useState<'agent' | 'dev'>('dev')
  const gridRef = useRef<HTMLDivElement>(null)
  const scrollYProgress = useMotionValue(0)

  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const update = () => {
      const top = el.getBoundingClientRect().top
      const vh = window.innerHeight
      const progress =
        1 - Math.min(1, Math.max(0, (top - vh * 0.5) / (vh * 0.5)))
      scrollYProgress.set(progress)
    }
    window.addEventListener('scroll', update, { passive: true })
    update()
    return () => window.removeEventListener('scroll', update)
  }, [scrollYProgress])

  const card2Y = useTransform(scrollYProgress, [0, 1], ['50%', '0%'])
  const card3Y = useTransform(scrollYProgress, [0, 1], ['100%', '0%'])
  const card2Opacity = useTransform(scrollYProgress, [0, 1], [0.5, 1])
  const card3Opacity = useTransform(scrollYProgress, [0, 1], [0.25, 1])

  const BORDER_BRIGHT = 'rgba(45, 212, 191, 0.85)'
  const BORDER_MID = 'rgba(45, 212, 191, 0.425)'
  const BORDER_FAINT = 'rgba(45, 212, 191, 0.255)'
  const BORDER_DIM = 'rgba(19, 78, 74, 0.25)'
  const card1Border = useTransform(
    scrollYProgress,
    [0, 0.2, 0.45],
    [BORDER_BRIGHT, BORDER_BRIGHT, BORDER_FAINT],
  )
  const card2Border = useTransform(
    scrollYProgress,
    [0.1, 0.33, 0.5, 0.67, 0.9],
    [BORDER_DIM, BORDER_DIM, BORDER_BRIGHT, BORDER_BRIGHT, BORDER_MID],
  )
  const card3Border = useTransform(
    scrollYProgress,
    [0.5, 0.72, 1],
    [BORDER_DIM, BORDER_DIM, BORDER_BRIGHT],
  )

  return (
    <>
      <motion.div
        id="start-now"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        className="relative mx-auto mt-24 mb-6 scroll-mt-24 md:mt-42"
      >
        <SectionHeader
          title="How to start"
          pretitle="Start now"
          srOnlyFullHeading="Start now How to start"
        />
      </motion.div>

      <div
        className="my-4 w-full overflow-hidden rounded-full"
        style={{ height: '1px' }}
      >
        <motion.div
          className="h-full bg-linear-to-r from-teal-900/40 to-teal-400"
          style={{
            scaleX: useTransform(scrollYProgress, [0, 0.85], [0, 1]),
            opacity: useTransform(scrollYProgress, [0.7, 1], [1, 0], {
              ease: easeOut,
            }),
            transformOrigin: 'left',
          }}
        />
      </div>

      <motion.div
        ref={gridRef}
        variants={staggerContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true, amount: 0.3 }}
        className="font-display relative z-20 grid grid-cols-1 gap-3 lg:grid-cols-3"
      >
        <motion.div
          variants={fadeInUp}
          className="col-span-1 space-y-2 rounded-xl border bg-teal-600/10 p-6 backdrop-blur-md"
          style={{ borderColor: card1Border }}
        >
          <p className="text-slate-300 text-shadow-sm">
            <span className="flex items-center gap-3 text-2xl text-white">
              <StepBadge number={1} />
              Sign up and get an API key
            </span>
            <br />
            Your first step is to obtain an{' '}
            <a
              href={NavigationData.signup.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline underline-offset-2 hover:text-blue-300"
            >
              Imandra Universe API key
            </a>{' '}
            that your instance of CodeLogician will use for connecting.
          </p>
          <ButtonLG
            href={NavigationData.signup.href}
            variant="none"
            ariaLabel={NavigationData.signup['aria-label']}
            classNames="lg:ml-auto mt-2 font-display bg-teal-600 text-white font-bold whitespace-nowrap hover:bg-emerald-500 max-w-max"
          >
            <FontAwesomeIcon icon={faQrcode} className="h-4 w-4" />
            Sign up
          </ButtonLG>
        </motion.div>

        <motion.div
          style={{ y: card2Y, opacity: card2Opacity }}
          className="h-full"
        >
          <motion.div
            variants={fadeInUp}
            className="col-span-1 h-full space-y-4 rounded-xl border bg-teal-600/10 p-6 backdrop-blur-md"
            style={{ borderColor: card2Border }}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3 text-2xl text-white">
                <StepBadge number={2} />
                Install
              </span>
              <div className="flex rounded-lg border border-slate-700/50 bg-slate-800/50 p-0.5">
                <button
                  onClick={() => setMode('dev')}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                    mode === 'dev'
                      ? 'bg-teal-600 text-white shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  curl
                </button>
                <button
                  onClick={() => setMode('agent')}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                    mode === 'agent'
                      ? 'bg-teal-600 text-white shadow'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  pip
                </button>
              </div>
            </div>
            {mode !== 'agent' ? (
              <div className="space-y-2">
                <TerminalBox
                  className="text-xs leading-snug"
                  copyContent="curl -fsSL https://codelogician.dev/codelogician/install.sh | sh"
                >
                  <span className="my-1 block">
                    <span className="text-teal-400">curl</span>
                    <span className="text-orange-400"> -fsSL </span>
                    <span className="text-blue-400">
                      codelogician.dev/codelogician/install.sh
                    </span>
                    <span className="text-slate-400"> | </span>
                    <span className="text-teal-400">sh</span>
                  </span>
                </TerminalBox>
                <p className="text-sm text-slate-400">Includes Python 3.13</p>
              </div>
            ) : (
              <div className="space-y-2">
                <TerminalBox copyContent="pip install codelogician">
                  <span className="my-1 block">
                    <span className="text-teal-400">pip </span>
                    <span className="text-orange-400">install </span>
                    <span className="text-blue-400">codelogician</span>
                  </span>
                </TerminalBox>
                <p className="text-sm text-slate-400">Requires Python 3.12+</p>
              </div>
            )}
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: card3Y, opacity: card3Opacity }}
          className="h-full"
        >
          <motion.div
            variants={fadeInUp}
            className="col-span-1 h-full space-y-4 rounded-xl border bg-teal-600/10 p-6 backdrop-blur-md"
            style={{ borderColor: card3Border }}
          >
            <span className="mb-4 flex items-center gap-3 text-2xl text-white">
              <StepBadge number={3} />
              Start reasoning
            </span>
            {mode === 'dev' ? (
              <div className="space-y-2">
                <TerminalBox copyButton={false}>
                  <span className="my-1 block">
                    <span className="text-slate-500">
                      Grok/Gemini/ChatGPT/{' '}
                    </span>
                    Claude use CodeLogician to find edge cases in this{' '}
                    <TypewriterWord />
                  </span>
                </TerminalBox>
                <p className="text-sm text-slate-400">
                  Your agent calls CodeLogician to analyze, verify, and reason
                  about code.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <TerminalBox copyContent="codelogician eval check model.iml">
                  <span className="my-1 block">
                    <span className="text-teal-400">codelogician</span>
                    <span className="text-slate-400"> eval </span>
                    <span className="text-blue-400">check</span>
                    <span className="text-slate-200"> model.iml</span>
                  </span>
                  <span className="text-xs text-slate-500">
                    # → Eval success!
                  </span>
                </TerminalBox>
                <p className="text-sm text-slate-400">
                  Evaluate models, verify properties, and decompose behavior
                  from the terminal.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.p
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        className="mx-auto mt-12 max-w-6xl text-base text-slate-600 dark:text-slate-300"
      >
        Read the full{' '}
        <a
          href="/docs/getting-started/"
          className="text-blue-400 underline-offset-2 hover:underline"
        >
          getting started guide here
        </a>
        .
      </motion.p>
    </>
  )
}
